import { forwardRef, useRef, useState, useEffect, useCallback } from 'preact/compat';
import { ChevronLeftIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { logNavInteraction, NAV_LOCATIONS, NAV_SECTIONS, NAV_ELEMENT_TYPES, NAV_ACTIONS } from 'utils';
import NavPromoCard from './NavPromoCard';
// import { LinkButton, BUTTON_SCHEMES, BUTTON_SIZES } from "./LinkButton";
import NewsletterForm from './NewsletterForm';

const BASE_NAV_INTERACTION_CONFIG = {
    location: NAV_LOCATIONS.PRIMARY,
    depth: 2,
};

const SubDrawer = forwardRef(({ isOpen, onClose, onCloseNavDrawer, onResetScroll, navItem, activeSubDrawerKey}, ref) => {
  const [activeLink, setActiveLink] = useState(null);
  const pillContainerRef = useRef(null);

const resetPillScroll = useCallback(() => {
  if (pillContainerRef.current?.parentNode) {
    pillContainerRef.current.parentNode.scrollTo({ 
      left: 0, 
      behavior: 'auto' 
    });
  }
}, []);

  // Reset everything when drawer closes
  useEffect(() => {
    if (!isOpen) {
      resetPillScroll();
      setActiveLink(null);
    }
  }, [isOpen, resetPillScroll]);


  const resetScrollPosition = useCallback(() => {
    setTimeout(() => {
      if (ref.current) ref.current.scrollTop = 0;
      resetPillScroll();
    }, 400);
  }, [ref, resetPillScroll]);

  useEffect(() => {
    if (onResetScroll) {
      onResetScroll(resetScrollPosition);
    }
  }, [onResetScroll, resetScrollPosition]);


  const handlePillClick = (e, item, navInteractionConfig) => {
    e.stopPropagation();
    setActiveLink(item.label);

    if (!pillContainerRef.current) return;

    const pill = e.currentTarget;
    const scrollContainer = pillContainerRef.current.parentNode; // Get the overflow container

    if (!scrollContainer) return;

    scrollContainer.scrollTo({
      left: pill.offsetLeft - 16, // Adjust for padding if needed
      behavior: 'smooth'
    });

    logNavInteraction({
      ...BASE_NAV_INTERACTION_CONFIG,
      ...navInteractionConfig,
    });
  };


  const handleClose = (navInteractionConfig) => {
    onCloseNavDrawer();
    resetScrollPosition();

    logNavInteraction({
      ...BASE_NAV_INTERACTION_CONFIG,
      ...navInteractionConfig,
    });
  };


  const handleCloseSubdrawer = (navInteractionConfig) => {
    onClose();
    resetScrollPosition();

    logNavInteraction({
      ...BASE_NAV_INTERACTION_CONFIG,
      ...navInteractionConfig,
    });
  };

  const handleFeaturedProductClick = (navInteractionConfig) => {
    onCloseNavDrawer();
    logNavInteraction({ 
      ...BASE_NAV_INTERACTION_CONFIG,
      ...navInteractionConfig,
    });
  };

  let featuredLinksOrderCounter = 0;
  let dropdownContentOrderCounter = 0;


  //Get live content
  const liveFeaturedLinks = navItem?.featured_links?.filter(link => link.isLive);
  const livePromoCards = navItem?.promo_cards?.filter(card => card.isLive);
  const liveFeaturedProducts = navItem?.featured_products?.filter(product => product.isLive);

  return (
    <div
      onClick={(e) => e.stopPropagation()} 
      role="dialog"
      aria-modal="true"
      aria-labelledby="subdrawer-title"
      aria-hidden={!isOpen}
      ref={ref}
      className={`tw-ease tw-fixed tw-left-auto tw-right-0 tw-top-0 tw-z-50 tw-min-h-dvh tw-w-[300px] tw-transform tw-self-end tw-overflow-auto tw-scroll-smooth tw-bg-white tw-px-8 tw-pb-16
        tw-transition-transform tw-duration-300 tw-scrollbar-none
        ${isOpen ? 'tw-translate-x-0' : 'tw-translate-x-full'}`}
      style={{
        pointerEvents: isOpen ? 'auto' : 'none',
        maxHeight: '100dvh', 
      }}
    >
      <div className='tw-sticky tw-top-0 tw-z-[5] tw-size-full tw-bg-white tw-pt-8'>
        <div className="tw-flex tw-w-full tw-items-center tw-justify-between">
          <button className="tw-flex tw-items-center tw-gap-4 tw-py-10 tw-font-semibold tw-text-[#3F4443]" onClick={() => handleCloseSubdrawer({
            section: NAV_SECTIONS.PRIMARY.MOBILE.MENU_BACK, 
            order: 1, 
            elementType: NAV_ELEMENT_TYPES.TEXT, 
            text: 'Back',
            action: NAV_ACTIONS.CLOSE_SUBMENU,
          })}>
            <ChevronLeftIcon className="tw-size-24" />
            <span className='tw-text-14 tw-font-semibold tw-leading-16'>Back</span>
          </button>
          <div className='tw-p-10 tw-text-[#3F4443]'>
            <XMarkIcon className='tw-size-24' onClick={() => handleClose({
              section: NAV_SECTIONS.PRIMARY.MOBILE.MENU_TOGGLE, 
              order: 1, 
              elementType: NAV_ELEMENT_TYPES.ICON, 
              text: 'Close',
              action: NAV_ACTIONS.CLOSE_SUBMENU,
            })} />
          </div>
        </div>
        <div className='tw-my-24 tw-flex tw-h-[44px] tw-items-center tw-justify-between tw-px-8'>
          <h2 className="tw-text-[20px] tw-font-bold tw-capitalize tw-leading-[28px] tw-tracking-[-0.4px] tw-text-[#3F4443]">
            {activeSubDrawerKey}
          </h2>
          {navItem?.featured_links?.filter((item) => item.label.startsWith('Shop All')).map((featuredLink, index) => {
            const featuredLinksOrder = ++featuredLinksOrderCounter;
            return <a 
              href={featuredLink.link} 
              key={index} 
              className="tw-flex tw-h-[44px] tw-items-center tw-gap-8 tw-text-14 tw-leading-16"
              onClick={() => logNavInteraction({
                ...BASE_NAV_INTERACTION_CONFIG,
                section: navItem.featuredLinksNavSectionName, 
                order: featuredLinksOrder, 
                elementType: NAV_ELEMENT_TYPES.TEXT, 
                text: featuredLink.label, 
                action: NAV_ACTIONS.LINK,
              })}
            >
              {featuredLink.label}
            </a>
          })}
        </div>
        
        {/* Pills Container */}
        {navItem?.category_groups.length > 0 && (
          <div className="tw-w-full tw-overflow-x-auto tw-px-8"> 
            <span className='tw-block tw-text-12 tw-leading-14'>
              {navItem.label == 'Bottles' ? 'Style:' : 'Jump to:'}
            </span>
            <div 
              ref={pillContainerRef}
              className='tw-my-6 tw-flex tw-w-max tw-gap-8 tw-overflow-x-auto tw-overflow-y-hidden tw-whitespace-nowrap tw-pb-24 tw-scrollbar-none'
            >
              {navItem.category_groups.map((item, index) => (
                <a 
                  key={item.label} 
                  onClick={(e) => handlePillClick(e, item, {
                    section: NAV_SECTIONS.PRIMARY.MOBILE.JUMP_TO, 
                    order: index + 1, 
                    elementType: NAV_ELEMENT_TYPES.PILL, 
                    text: item.label,
                    action: NAV_ACTIONS.SCROLL_TO,
                  })}
                  href={`#${item.label}`} 
                  className={`${activeLink === item.label ? 'tw-border-[#00A3E0] tw-bg-[#00A3E0] tw-text-white' : 'tw-text-[#8E9593]'} tw-whitespace-nowrap tw-rounded-50 tw-border tw-border-solid tw-border-[#F3F3F7] tw-px-12 tw-py-8 tw-text-14 tw-font-semibold`}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {activeSubDrawerKey && 
        <div>
          {liveFeaturedLinks.length > 0 && liveFeaturedLinks[0]?.text != 'Shop All Extras' && ( 
            <div className="tw-mb-24 tw-grid tw-grid-cols-2 tw-gap-20 tw-px-8">
              {liveFeaturedLinks.filter((item) => !item.label.startsWith('Shop All')).map((featuredLink, index) => {
                const featuredLinksOrder = ++featuredLinksOrderCounter;
                if (featuredLink.mobile && window.innerWidth <= 768) return null; // Skip if mobile only and on mobile view
                return !featuredLink.mobile && (
                    <a key={index} href={featuredLink.link} onClick={() => logNavInteraction({
                      ...BASE_NAV_INTERACTION_CONFIG,
                      section: navItem.featuredLinksNavSectionName, 
                      order: featuredLinksOrder, 
                      elementType: NAV_ELEMENT_TYPES.IMAGE, 
                      text: featuredLink.label, 
                      action: NAV_ACTIONS.LINK,
                    })}>
                      <img
                        src={featuredLink.image}
                        alt={featuredLink.imageAlt ? featuredLink.imageAlt : featuredLink.label}
                        className="tw-mb-8 tw-h-[124px] tw-w-full tw-rounded-8 tw-object-cover"
                      />
                      <p className="tw-mb-8 tw-text-16 tw-font-bold">
                        {featuredLink.label.includes('®') ? (
                          <>
                            {featuredLink.label.split('®')[0]}
                            <sup className="tw-relative tw-top-[.15em] tw-align-super tw-text-[0.7em]">®</sup>
                            {featuredLink.label.split('®')[1]}
                          </>
                        )
                        : 
                        (featuredLink.label)
                      }
                      </p>
                      <p className="tw-text-14">{featuredLink.description}</p>
                    </a>
                )
              })}
            </div>
         )}

          <ul className="tw-flex tw-flex-col tw-gap-24 tw-px-8">
            {liveFeaturedProducts.length > 0 && liveFeaturedProducts.map((item, index) => (
              <li key={item.label} className={'tw-scroll-mt-[230px]'} onClick={() => handleFeaturedProductClick({
                section: navItem.featuredProductsNavSectionName, 
                order: index + 1,
                elementType: NAV_ELEMENT_TYPES.IMAGE,
                text: item.label, 
                action: NAV_ACTIONS.LINK,
              })}>
                <a href={item.link}>
                  <div>
                    <img
                      className="tw-aspect-[9/4] tw-h-[144px] tw-w-full tw-rounded-8 tw-object-cover"
                      src={item.mobileImage ? item.mobileImage : item.desktopImage}
                      alt={item.imageAlt ? item.imageAlt : item.label}
                    />
                    <h3 className="tw-my-[.5rem] tw-flex tw-items-center tw-text-16 tw-font-bold tw-leading-18 tw-text-[#3F4443]">
                      {item.label.includes('®') ? (
                        <>
                          {item.label.split('®')[0]}
                          <sup className="tw-relative tw-top-[-.25em] tw-align-super tw-text-[0.7em]">®</sup>
                          {item.label.split('®')[1]}
                        </>
                      ) : (
                        item.label
                      )}   
                      {item.isNew && !item.customLabel && (
                        <span className="tw--top-[0.15rem] tw-ml-8 tw-text-12 tw-font-semibold tw-uppercase tw-leading-[14px] tw-text-[#00A3E0]">
                          *New*
                        </span>
                        )}
                      {item.customLabel && (
                        <span className="tw--top-[0.15rem] tw-ml-8 tw-text-12 tw-font-semibold tw-uppercase tw-leading-[14px] tw-text-[#00A3E0]">
                          {item.customLabel}
                        </span>
                      )}
                    </h3>
                    <p className="tw-text-14 tw-text-[#3F4443]">
                      {item.textContent}
                    </p>
                    {item.disclaimer && 
                      <span className="tw-block tw-text-12 tw-text-[#00A3E0]">{item.disclaimer}</span>
                    }
                  </div>
                </a>
              </li>
            ))}
            
            {navItem?.category_groups.length > 0 && navItem.category_groups.map((item, index) => {
              const dropdownContentItemOrder = ++dropdownContentOrderCounter;
              return <li key={index} id={item.label} className={'tw-scroll-mt-[230px]'} onClick={() => onCloseNavDrawer()}>
                <a href={item.link} onClick={() => logNavInteraction({
                  ...BASE_NAV_INTERACTION_CONFIG,
                  section: navItem.dropdownContentNavSectionName, 
                  order: dropdownContentItemOrder, 
                  elementType: NAV_ELEMENT_TYPES.IMAGE, 
                  text: item.label, 
                  action: NAV_ACTIONS.LINK,
                })}>
                  <img
                    className="tw-aspect-[9/4] tw-h-[126px] tw-w-full tw-rounded-8 tw-object-cover"
                    src={item.mobileImage ? item.mobileImage : item.image}
                    alt={item.imageAlt ? item.imageAlt : item.label}
                  />
                  <div className='tw-flex tw-items-center tw-justify-between'>
                    <h3 className="tw-my-[.5rem] tw-flex tw-items-center tw-text-16 tw-font-bold tw-leading-18 tw-text-[#3F4443]">
                  {item.label.includes('®') ? (
                    <>
                      {item.label.split('®')[0]}
                      <sup className="tw-relative tw-top-[-.25em] tw-align-super tw-text-[0.7em]">®</sup>
                      {item.label.split('®')[1]}
                    </>
                  ) : (
                    item.label
                  )}                        
                {item.isNew && 
                      <span className="tw--top-[0.15rem] tw-ml-8 tw-text-12 tw-font-semibold tw-uppercase tw-leading-[14px] tw-text-[#00A3E0]">
                        *New*
                      </span>
                      }
                    </h3>
                  </div>
                  {item.description &&
                    <p className="tw-text-14 tw-text-[#3F4443]">
                      {item.description}
                    </p>
                  }
                  {item.disclaimer && 
                    <span className="tw-block tw-text-12 tw-text-[#00A3E0]">{item.disclaimer}</span>
                  }
                </a>

                {item.subLinks && item.subLinks.map((subLink, subIndex) => {
                  const dropdownContentSubItemOrder = ++dropdownContentOrderCounter;

                  return <div key={subIndex} className="tw-flex tw-h-[44px] tw-items-center tw-gap-8">
                    <a href={subLink.link} className="tw-flex tw-size-full tw-items-center tw-text-14 tw-font-semibold tw-leading-16" onClick={() => logNavInteraction({
                      ...BASE_NAV_INTERACTION_CONFIG,
                      section: navItem.dropdownContentNavSectionName, 
                      order: dropdownContentSubItemOrder, 
                      elementType: NAV_ELEMENT_TYPES.TEXT, 
                      text: subLink.label, 
                      action: NAV_ACTIONS.LINK,
                    })}>
                      {subLink.label.includes('®') ? (
                          <>
                            {subLink.label.split('®')[0]}
                            <sup className="tw-relative tw-top-[-.25em] tw-align-super tw-text-[0.7em]">®&nbsp;</sup>
                            {subLink.label.split('®')[1]}
                          </>
                        )
                        : 
                        (subLink.label)
                      }
                    </a>
                  </div>
                })}
              </li>
            })}
          </ul>

          {/* {navItem?.featuredLinks?.filter((item) => item.label.startsWith('Customize')).map((featuredLink) => {
            const featuredLinksOrder = ++featuredLinksOrderCounter;
            return <div className="tw-mx-8" key={featuredLink.label}>
              <LinkButton
                href={featuredLink.href}
                classNames="tw-max-w-[318px] tw-w-full tw-text-14 tw-leading-16 tw-mt-24"
                scheme={BUTTON_SCHEMES.PRIMARY}
                size={BUTTON_SIZES.MEDIUM}
                ariaLabel={"Explore Flavors"}
                onClick={() => logNavInteraction({
                  ...BASE_NAV_INTERACTION_CONFIG,
                  section: navItem.featuredLinksNavSectionName, 
                  order: featuredLinksOrder, 
                  elementType: NAV_ELEMENT_TYPES.BUTTON, 
                  text: featuredLink.label, 
                  action: NAV_ACTIONS.LINK,
                })}
              >
                {featuredLink.label}
              </LinkButton>
            </div>
          })} */}

          {navItem?.displayNewsletter && (
           <div className='tw-mt-24 tw-flex tw-w-full tw-flex-col tw-justify-center tw-px-8'>
            <NewsletterForm  signupFormLocation={'Mobile Nav'} navInteractionConfig={{ 
              ...BASE_NAV_INTERACTION_CONFIG,
              section: navItem.newsletterNavSectionName, 
             }} />
            </div> 
          )}

          {livePromoCards.length > 0 && (
            <div className='tw-mt-24 tw-flex tw-flex-col tw-gap-20'>
              {livePromoCards.map((featuredPromoCard, idx) => (
                <div key={idx} className="tw-flex tw-flex-col tw-gap-20" onClick={() => onCloseNavDrawer()}> 
                  <NavPromoCard
                    key={idx}
                    image={featuredPromoCard.image}
                    label={featuredPromoCard.title}
                    link={featuredPromoCard.link}
                    gradient={featuredPromoCard.gradient}
                    navInteractionConfig={{ 
                      ...BASE_NAV_INTERACTION_CONFIG,
                      section: navItem.featuredPromoCardsNavSectionName, 
                      order: idx + 1,
                    }}
                  />
              </div>
              ))}
            </div>
          )}
        </div>
      }
    </div>
  );
});

export default SubDrawer;